// Cache Initialization Service
// This service warms up caches in the background when the app starts
import DeviceService from './deviceService';
import locationService from './locationService';

/**
 * Cache Initializer - Manages background cache warming and automatic refresh
 */
class CacheInitializer {
  static #initialized = false;
  static #initPromise = null;
  static #refreshIntervals = {
    devices: null,
    locations: null,
  };
  static #idleRefreshInterval = null;
  static #idleCheckInterval = null;
  static #isUserIdle = false;
  static #lastActivity = Date.now();
  static #idleThreshold = 5 * 60 * 1000; // 5 minutes of inactivity = idle

  /**
   * Initialize all caches in the background
   * This should be called once when the app starts (after authentication)
   * @param {Object} options - Configuration options
   * @param {boolean} options.enableAutoRefresh - Enable automatic background refresh (default: true)
   * @param {number} options.deviceRefreshInterval - Device cache refresh interval in ms (default: 2 minutes)
   * @param {number} options.locationRefreshInterval - Location cache refresh interval in ms (default: 5 minutes)
   * @returns {Promise<void>}
   */
  static async initializeAll(options = {}) {
    const {
      enableAutoRefresh = true,
      deviceRefreshInterval = 2 * 60 * 1000, // 2 minutes for devices (they change more frequently)
      locationRefreshInterval = 5 * 60 * 1000, // 5 minutes for locations (they change less often)
    } = options;

    // Prevent multiple initializations
    if (this.#initialized) {
      console.log('[CacheInitializer] Already initialized');
      return;
    }

    // If already initializing, return the existing promise
    if (this.#initPromise) {
      console.log('[CacheInitializer] Already initializing, waiting...');
      return this.#initPromise;
    }

    console.log('[CacheInitializer] Starting cache initialization...');

    this.#initPromise = (async () => {
      try {
        // Initialize caches in parallel for faster startup
        await Promise.allSettled([
          this.initializeDeviceCache(),
          this.initializeLocationCache(),
        ]);

        this.#initialized = true;
        console.log('[CacheInitializer] All caches initialized successfully');

        // Start automatic background refresh if enabled
        if (enableAutoRefresh) {
          this.startAutoRefresh(deviceRefreshInterval, locationRefreshInterval);
        }

        // Start idle detection
        this.startIdleDetection();
      } catch (error) {
        console.error('[CacheInitializer] Error during cache initialization:', error);
        // Don't throw - allow the app to continue even if cache warming fails
      } finally {
        this.#initPromise = null;
      }
    })();

    return this.#initPromise;
  }

  /**
   * Initialize device cache
   * @private
   */
  static async initializeDeviceCache() {
    try {
      console.log('[CacheInitializer] Warming up device cache...');
      await DeviceService.getAllDevices({
        useCache: false, // Force fresh fetch to populate cache
        forceRefresh: true
      });
      console.log('[CacheInitializer] Device cache warmed up');
    } catch (error) {
      console.error('[CacheInitializer] Failed to initialize device cache:', error);
      // Don't throw - allow other caches to initialize
    }
  }

  /**
   * Initialize location cache
   * @private
   */
  static async initializeLocationCache() {
    try {
      console.log('[CacheInitializer] Warming up location cache...');
      await locationService.getLocations({
        useCache: false, // Force fresh fetch to populate cache
        forceRefresh: true
      });
      console.log('[CacheInitializer] Location cache warmed up');
    } catch (error) {
      console.error('[CacheInitializer] Failed to initialize location cache:', error);
      // Don't throw - allow other caches to initialize
    }
  }

  /**
   * Refresh all caches
   * Call this when data might have changed (e.g., after mutations)
   */
  static async refreshAll() {
    console.log('[CacheInitializer] Refreshing all caches...');

    try {
      await Promise.allSettled([
        DeviceService.getAllDevices({ forceRefresh: true }),
        locationService.getLocations({ forceRefresh: true }),
      ]);

      console.log('[CacheInitializer] All caches refreshed');
    } catch (error) {
      console.error('[CacheInitializer] Error refreshing caches:', error);
    }
  }

  /**
   * Invalidate all caches
   */
  static invalidateAll() {
    console.log('[CacheInitializer] Invalidating all caches...');
    DeviceService.invalidateCache();
    locationService.constructor.invalidateCache();
    this.#initialized = false;
  }

  /**
   * Check if caches are initialized
   */
  static isInitialized() {
    return this.#initialized;
  }

  /**
   * Start automatic background cache refresh
   * @param {number} deviceInterval - Device cache refresh interval in ms
   * @param {number} locationInterval - Location cache refresh interval in ms
   */
  static startAutoRefresh(deviceInterval, locationInterval) {
    console.log(`[CacheInitializer] Starting auto-refresh (devices: ${deviceInterval/1000}s, locations: ${locationInterval/1000}s)`);

    // Stop any existing intervals
    this.stopAutoRefresh();

    // Device cache refresh - more frequent as device statuses change often
    this.#refreshIntervals.devices = setInterval(async () => {
      if (!this.#isUserIdle) {
        console.log('[CacheInitializer] Auto-refreshing device cache (user active)');
        try {
          await DeviceService.getAllDevices({ forceRefresh: true });
        } catch (error) {
          console.error('[CacheInitializer] Device auto-refresh failed:', error);
        }
      }
    }, deviceInterval);

    // Location cache refresh - less frequent as locations change rarely
    this.#refreshIntervals.locations = setInterval(async () => {
      if (!this.#isUserIdle) {
        console.log('[CacheInitializer] Auto-refreshing location cache (user active)');
        try {
          await locationService.getLocations({ forceRefresh: true });
        } catch (error) {
          console.error('[CacheInitializer] Location auto-refresh failed:', error);
        }
      }
    }, locationInterval);

    // When user becomes idle, do a slower refresh (every 10 minutes)
    this.#idleRefreshInterval = setInterval(async () => {
      if (this.#isUserIdle) {
        console.log('[CacheInitializer] Auto-refreshing caches (user idle)');
        try {
          await Promise.allSettled([
            DeviceService.getAllDevices({ forceRefresh: true }),
            locationService.getLocations({ forceRefresh: true }),
          ]);
        } catch (error) {
          console.error('[CacheInitializer] Idle auto-refresh failed:', error);
        }
      }
    }, 10 * 60 * 1000); // 10 minutes for idle refresh
  }

  /**
   * Stop automatic background cache refresh
   */
  static stopAutoRefresh() {
    if (this.#refreshIntervals.devices) {
      clearInterval(this.#refreshIntervals.devices);
      this.#refreshIntervals.devices = null;
    }
    if (this.#refreshIntervals.locations) {
      clearInterval(this.#refreshIntervals.locations);
      this.#refreshIntervals.locations = null;
    }
    if (this.#idleRefreshInterval) {
      clearInterval(this.#idleRefreshInterval);
      this.#idleRefreshInterval = null;
    }
    console.log('[CacheInitializer] Auto-refresh stopped');
  }

  /**
   * Start idle detection
   * Monitors user activity to adjust refresh behavior
   */
  static startIdleDetection() {
    // Track user activity
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      this.#lastActivity = Date.now();
      if (this.#isUserIdle) {
        this.#isUserIdle = false;
        console.log('[CacheInitializer] User active again');
      }
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check idle status every minute
    const idleCheckInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - this.#lastActivity;
      const wasIdle = this.#isUserIdle;
      this.#isUserIdle = timeSinceActivity > this.#idleThreshold;

      if (!wasIdle && this.#isUserIdle) {
        console.log('[CacheInitializer] User became idle');
      }
    }, 60 * 1000); // Check every minute

    // Store interval for cleanup
    this.#idleCheckInterval = idleCheckInterval;
  }

  /**
   * Stop idle detection
   */
  static stopIdleDetection() {
    if (this.#idleCheckInterval) {
      clearInterval(this.#idleCheckInterval);
      this.#idleCheckInterval = null;
    }
  }

  /**
   * Reset initialization state (useful for testing or re-authentication)
   */
  static reset() {
    this.stopAutoRefresh();
    this.stopIdleDetection();
    this.#initialized = false;
    this.#initPromise = null;
    this.#isUserIdle = false;
    this.#lastActivity = Date.now();
    console.log('[CacheInitializer] Reset complete');
  }
}

export default CacheInitializer;
